import { useEffect, useState } from "react";
import { useCockpit } from "../../lib/state";
import { useApprovals, useHeartbeats } from "../../lib/queries";
import { relativeTime } from "../../lib/format";

export default function Header() {
  const setPaletteOpen = useCockpit((s) => s.setPaletteOpen);
  const refreshTimestamp = useCockpit((s) => s.refreshTimestamp);
  const { data: approvals } = useApprovals();
  const { data: heartbeats } = useHeartbeats();

  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, []);

  const pending = (approvals ?? []).filter((a) => a.status === "pending").length;
  const overseer = (heartbeats ?? []).find((h) => h.source === "overseer");
  const dispatcher = (heartbeats ?? []).find((h) => h.source === "dispatcher");
  const lastBeat = overseer?.created_at ?? dispatcher?.created_at ?? null;
  const beatAgeMs = lastBeat ? Date.now() - new Date(lastBeat).getTime() : Infinity;
  const live = beatAgeMs < 5 * 60_000;

  return (
    <header
      className="aero-glass relative mx-3 mt-3 flex items-center justify-between px-5"
      style={{ height: 56, borderRadius: 22 }}
    >
      <div className="flex items-center gap-3">
        <span
          className="text-xl"
          style={{
            fontFamily: "Fraunces, serif",
            fontStyle: "italic",
            fontWeight: 700,
            letterSpacing: "-0.01em",
          }}
        >
          <span className="holo-text">Meanie</span>
        </span>
        <span
          className="font-eyebrow"
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--hot-pink)",
            opacity: 0.7,
          }}
        >
          ✦ Control Panel
        </span>
        <span
          className="glossy-pill glossy-pill--small"
          style={
            live
              ? undefined
              : {
                  background: "linear-gradient(180deg, rgba(255,255,255,.85), rgba(255,255,255,.55))",
                  color: "#d4a04c",
                  textShadow: "none",
                }
          }
          title={lastBeat ? `last heartbeat ${relativeTime(lastBeat)}` : "no heartbeats yet"}
        >
          <span
            className={`glossy-radial-bubble${live ? " pulse-dot" : ""}`}
            style={{
              width: 8,
              height: 8,
              color: live ? "#7ec699" : "#d4a04c",
            }}
          />
          MOM-E {live ? "LIVE" : "STALE"}
        </span>
      </div>

      <div className="flex items-center gap-3">
        {pending > 0 ? (
          <span
            className="glossy-pill glossy-pill--small"
            style={{
              background: "linear-gradient(180deg, #ff8aa1, #cf6679)",
            }}
          >
            ★ {pending} awaiting
          </span>
        ) : null}
        <span
          className="font-eyebrow"
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--plum)",
            opacity: 0.7,
          }}
          title="last cache refresh"
        >
          refreshed {relativeTime(new Date(refreshTimestamp).toISOString())}
        </span>
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className="glossy-pill glossy-pill--ghost"
          aria-label="Open command palette"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          Search
          <span className="kbd">⌘</span>
          <span className="kbd">K</span>
        </button>
      </div>
    </header>
  );
}
